"use client";

import { useCallback, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@workspace/ui/components/select";
import { Slider } from "@workspace/ui/components/slider";
import { Switch } from "@workspace/ui/components/switch";
import { Label } from "@workspace/ui/components/label";
import { Button } from "@workspace/ui/components/button";
import { Check, RotateCcw } from "lucide-react";
import type {
  ColourableElement,
  DocTheme,
  FontFamilyId,
  HeadingLevel,
  ThemeOverrides,
} from "@/lib/theme";
import {
  PRESET_LIST,
  FONT_LABELS,
  LIMITS,
  getPreset,
  HEADING_LEVELS,
  COLOURABLE_ELEMENTS,
} from "@/lib/theme";

import type { ColorSuggestion } from "@/lib/theme/color-suggestions";
import { toHex } from "@/lib/theme/color-convert";
import { ColorPicker } from "./color-picker";
import { ThemeScope } from "./theme-scope";
import type { useDocTheme } from "@/lib/theme/use-doc-theme";

type ThemeState = ReturnType<typeof useDocTheme>;

/** Short labels for the "Also apply to" toggles — must fit a 352px row. */
const ELEMENT_LABELS: Record<ColourableElement, string> = {
  strong: "Bold",
  blockquote: "Quote",
  code: "Code",
};

const FONT_OPTIONS = Object.entries(FONT_LABELS) as [FontFamilyId, string][];

const WEIGHT_OPTIONS = [
  { value: 300, label: "Light" },
  { value: 400, label: "Regular" },
  { value: 500, label: "Medium" },
  { value: 600, label: "Semibold" },
  { value: 700, label: "Bold" },
  { value: 800, label: "Extra bold" },
];

export function ThemePanel({ state }: { state: ThemeState }) {
  const { theme, isOverriding, setDocTheme, clearDocTheme, saveAsUserDefault } =
    state;

  // Editing always writes a per-doc override (clone the effective theme so the
  // first edit captures the inherited baseline).
  const effective = theme;
  // Memoised so the `{}` fallback keeps a stable identity — a fresh literal each
  // render would change `update`'s dependencies on every render.
  const ov: ThemeOverrides = useMemo(
    () => effective.overrides ?? {},
    [effective.overrides],
  );

  const update = useCallback(
    (patch: Partial<ThemeOverrides>) => {
      // An explicit `undefined` in the patch means "unset this knob", so the key
      // has to be REMOVED rather than spread in as undefined — `hasOverrides()`
      // counts keys, so a lingering `headingColor: undefined` would keep the
      // theme looking overridden (and deselect the preset card) after a reset.
      const merged: Partial<ThemeOverrides> = { ...ov, ...patch };
      for (const key of Object.keys(patch) as (keyof ThemeOverrides)[]) {
        if (patch[key] === undefined) delete merged[key];
      }
      setDocTheme({ preset: effective.preset, overrides: merged });
    },
    [effective.preset, ov, setDocTheme],
  );

  const selectPreset = useCallback(
    (presetId: string) => {
      // Picking a preset resets overrides to that preset's baseline.
      setDocTheme({ preset: presetId });
    },
    [setDocTheme],
  );

  // Resolved values for control state (preset baseline + overrides).
  const base = getPreset(effective.preset).base;
  const resolved = { ...base, ...ov };

  // Hex forms for the pickers. Presets store oklch, so these need real
  // conversion — see `toHex`.
  const hexes = {
    accent: toHex(resolved.accent),
    bodyColor: toHex(resolved.bodyColor),
    background: toHex(resolved.background),
    headingColor: toHex(resolved.headingColor ?? resolved.bodyColor),
  };

  // Shortcuts offered inside every colour picker. Ordered most-canonical first,
  // because `buildColorSuggestions` dedupes by value and keeps the first label —
  // so a heading colour still equal to body ink shows as "Body text", not
  // "Headings".
  //
  // These labels MUST match the `label` passed to each ColorRow below: a row
  // identifies its own palette entry by label so it is not offered back to
  // itself. Rename one, rename both.
  const palette: ColorSuggestion[] = [
    { label: "Accent", hex: hexes.accent },
    { label: "Body text", hex: hexes.bodyColor },
    { label: "Background", hex: hexes.background },
    { label: "Headings", hex: hexes.headingColor },
  ];

  return (
    <div className="flex flex-col gap-5 overflow-y-auto px-4 py-3 text-sm">
      {/* Presets */}
      <section>
        <SectionTitle>Preset</SectionTitle>
        <div className="grid grid-cols-2 gap-2">
          {PRESET_LIST.map((p) => (
            <PresetCard
              key={p.id}
              presetId={p.id}
              name={p.name}
              description={p.description}
              active={effective.preset === p.id && !hasOverrides(ov)}
              onSelect={() => selectPreset(p.id)}
            />
          ))}
        </div>
      </section>

      {/* Colours */}
      <section className="flex flex-col gap-3">
        <SectionTitle>Colours</SectionTitle>
        <ColorRow
          label="Accent"
          value={hexes.accent}
          onChange={(hex) => update({ accent: hex })}
          suggestions={palette}
        />
        <ColorRow
          label="Body text"
          value={hexes.bodyColor}
          onChange={(hex) => update({ bodyColor: hex })}
          suggestions={palette}
        />
        <ColorRow
          label="Background"
          value={hexes.background}
          onChange={(hex) => update({ background: hex })}
          suggestions={palette}
        />

        {/* Headings default to body ink; the swatch is only meaningful once set,
            so the level/element toggles stay hidden until then rather than
            offering choices that cannot show any effect. */}
        <ColorRow
          label="Headings"
          value={hexes.headingColor}
          onChange={(hex) => update({ headingColor: hex })}
          suggestions={palette}
          onClear={
            resolved.headingColor
              ? () =>
                  update({
                    headingColor: undefined,
                    headingColorLevels: undefined,
                    headingColorElements: undefined,
                  })
              : undefined
          }
        />
        {resolved.headingColor && (
          <>
            <ToggleRow
              label="Levels"
              options={HEADING_LEVELS.map((l) => ({
                key: String(l),
                label: `H${l}`,
                on: (resolved.headingColorLevels ?? HEADING_LEVELS).includes(l),
              }))}
              onToggle={(key, on) => {
                const current = resolved.headingColorLevels ?? HEADING_LEVELS;
                const level = Number(key) as HeadingLevel;
                const next = on
                  ? HEADING_LEVELS.filter(
                      (l) => current.includes(l) || l === level,
                    )
                  : current.filter((l) => l !== level);
                update({ headingColorLevels: next });
              }}
            />
            <ToggleRow
              label="Also"
              options={COLOURABLE_ELEMENTS.map((e) => ({
                key: e,
                label: ELEMENT_LABELS[e],
                on: (resolved.headingColorElements ?? []).includes(e),
              }))}
              onToggle={(key, on) => {
                const current = resolved.headingColorElements ?? [];
                const el = key as ColourableElement;
                const next = on
                  ? COLOURABLE_ELEMENTS.filter(
                      (e) => current.includes(e) || e === el,
                    )
                  : current.filter((e) => e !== el);
                update({ headingColorElements: next });
              }}
            />
          </>
        )}
      </section>

      {/* Fonts */}
      <section className="flex flex-col gap-3">
        <SectionTitle>Fonts</SectionTitle>
        <FontRow
          label="Body"
          value={resolved.fontBody}
          onChange={(v) => update({ fontBody: v })}
        />
        <FontRow
          label="Headings"
          value={resolved.fontHeading}
          onChange={(v) => update({ fontHeading: v })}
        />
        <FontRow
          label="Code"
          value={resolved.fontMono}
          onChange={(v) => update({ fontMono: v })}
        />
      </section>

      {/* Weights & style */}
      <section className="flex flex-col gap-3">
        <SectionTitle>Weight &amp; style</SectionTitle>
        <WeightRow
          label="Body weight"
          value={resolved.bodyWeight}
          onChange={(v) => update({ bodyWeight: v })}
        />
        <WeightRow
          label="Heading weight"
          value={resolved.headingWeight}
          onChange={(v) => update({ headingWeight: v })}
        />
        <div className="flex items-center justify-between">
          <Label className="text-muted-foreground">Italic headings</Label>
          <Switch
            checked={resolved.italicHeadings}
            onCheckedChange={(v) => update({ italicHeadings: v })}
          />
        </div>
      </section>

      {/* Sizing & spacing */}
      <section className="flex flex-col gap-4">
        <SectionTitle>Size &amp; spacing</SectionTitle>
        <SliderRow
          label="Base font size"
          value={resolved.baseFontSize}
          min={LIMITS.baseFontSize.min}
          max={LIMITS.baseFontSize.max}
          step={1}
          suffix="px"
          onChange={(v) => update({ baseFontSize: v })}
        />
        <SliderRow
          label="Heading scale"
          value={resolved.fontScale}
          min={LIMITS.fontScale.min}
          max={LIMITS.fontScale.max}
          step={0.01}
          format={(v) => `${v.toFixed(2)}×`}
          onChange={(v) => update({ fontScale: v })}
        />
        <SliderRow
          label="Spacing"
          value={resolved.spacing}
          min={LIMITS.spacing.min}
          max={LIMITS.spacing.max}
          step={0.05}
          format={(v) => `${v.toFixed(2)}×`}
          onChange={(v) => update({ spacing: v })}
        />
      </section>

      {/* Live preview */}
      <section className="flex flex-col gap-2">
        <SectionTitle>Preview</SectionTitle>
        <ThemePreview theme={effective} />
      </section>

      {/* Actions */}
      <section className="flex flex-col gap-2 border-t pt-3">
        <Button
          variant="secondary"
          size="sm"
          className="justify-start gap-2"
          onClick={() => saveAsUserDefault()}
        >
          <Check className="h-4 w-4" />
          Set as my default
        </Button>
        {isOverriding && (
          <Button
            variant="ghost"
            size="sm"
            className="justify-start gap-2 text-muted-foreground"
            onClick={clearDocTheme}
          >
            <RotateCcw className="h-4 w-4" />
            Reset to my default
          </Button>
        )}
      </section>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h4>
  );
}

function PresetCard({
  presetId,
  name,
  description,
  active,
  onSelect,
}: {
  presetId: string;
  name: string;
  description: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      title={description}
      className={`flex flex-col items-stretch gap-1 rounded-md border p-2 text-left transition-colors ${
        active
          ? "border-primary ring-1 ring-primary"
          : "border-border hover:border-muted-foreground/40"
      }`}
    >
      <ThemeScope
        theme={{ preset: presetId }}
        className="docs-editor pointer-events-none rounded-sm bg-muted/40 px-2 py-1.5"
      >
        {/* Heading + body inherit family/weight/colour from the scope's CSS. */}
        <span
          className="block leading-tight"
          style={{
            fontFamily: "var(--doc-font-heading, var(--doc-font-body, var(--font-sans)))",
            fontSize: "0.95rem",
            color: "var(--doc-accent, var(--primary))",
          }}
        >
          Aa
        </span>
        <span style={{ fontSize: "0.7rem" }}>The quick brown fox</span>
      </ThemeScope>
      <span className="text-xs font-medium">{name}</span>
    </button>
  );
}

function ColorRow({
  label,
  value,
  onChange,
  onClear,
  suggestions,
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  /** When given, shows a reset control — for knobs that can be "unset". */
  onClear?: () => void;
  /** Other colours in the theme, offered inside the picker popover. */
  suggestions?: ColorSuggestion[];
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Label className="shrink-0 text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-muted-foreground">{value}</span>
        {onClear && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClear}
            className="h-5 w-5 text-muted-foreground"
            aria-label={`Reset ${label.toLowerCase()} to body colour`}
            title="Reset to body colour"
          >
            <RotateCcw className="h-3 w-3" />
          </Button>
        )}
        <ColorPicker
          label={label}
          value={value}
          onChange={onChange}
          suggestions={suggestions}
        />
      </div>
    </div>
  );
}

/**
 * A row of small on/off pills. Used for the heading levels and the "also apply
 * to" elements — both are multi-select over a fixed, short option set, where
 * checkboxes would cost more width than the panel has (352px) and a Select
 * would hide the current state behind a click.
 */
function ToggleRow({
  label,
  options,
  onToggle,
}: {
  label: string;
  options: { key: string; label: string; on: boolean }[];
  onToggle: (key: string, on: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 pl-3">
      <Label className="shrink-0 text-xs text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-1">
        {options.map((o) => (
          <button
            key={o.key}
            type="button"
            role="switch"
            aria-checked={o.on}
            onClick={() => onToggle(o.key, !o.on)}
            className={`rounded border px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
              o.on
                ? "border-primary bg-primary/15 text-foreground"
                : "border-border text-muted-foreground hover:border-muted-foreground/40"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function FontRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: FontFamilyId;
  onChange: (v: FontFamilyId) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Label className="text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={(v) => onChange(v as FontFamilyId)}>
        <SelectTrigger className="h-8 w-44 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FONT_OPTIONS.map(([id, lbl]) => (
            <SelectItem key={id} value={id} className="text-xs">
              {lbl}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function WeightRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  // Snap the resolved weight to the nearest option for display.
  const nearest = WEIGHT_OPTIONS.reduce((a, b) =>
    Math.abs(b.value - value) < Math.abs(a.value - value) ? b : a,
  ).value;
  return (
    <div className="flex items-center justify-between gap-2">
      <Label className="text-muted-foreground">{label}</Label>
      <Select
        value={String(nearest)}
        onValueChange={(v) => onChange(Number(v))}
      >
        <SelectTrigger className="h-8 w-44 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {WEIGHT_OPTIONS.map((w) => (
            <SelectItem key={w.value} value={String(w.value)} className="text-xs">
              {w.label} ({w.value})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SliderRow({
  label,
  value,
  min,
  max,
  step,
  suffix,
  format,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix?: string;
  format?: (v: number) => string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-muted-foreground">{label}</Label>
        <span className="font-mono text-xs">
          {format ? format(value) : `${value}${suffix ?? ""}`}
        </span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={([v]) => onChange(v)}
      />
    </div>
  );
}

function ThemePreview({ theme }: { theme: DocTheme }) {
  return (
    <ThemeScope
      theme={theme}
      // `bg-background` dropped: ThemeScope now paints the surface via an
      // inline style, which wins over the utility class anyway, so it was dead.
      className="docs-editor rounded-md border p-3"
    >
      <h1>Heading one</h1>
      <h2>Heading two</h2>
      <p>
        Body text with a <a href="#preview">link</a>, some{" "}
        <strong>bold</strong> and <em>italic</em> words, plus{" "}
        <code>inline code</code>.
      </p>
      <blockquote>
        <p>A short blockquote to show the accent rule.</p>
      </blockquote>
    </ThemeScope>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function hasOverrides(ov: ThemeOverrides): boolean {
  return Object.keys(ov).length > 0;
}
